import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  BookOpen, Brain, Sparkles, Trophy, Flame, Calendar,
  ArrowRight, CheckCircle, Clock, Target, Star, Zap,
  Award, TrendingUp, Gift, Crown
} from 'lucide-react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

const LEVEL_CONFIG = {
  'A1': { name: 'Başlangıç', emoji: '🌱', desc: 'İlk adımlar!', color: 'green', gradient: 'from-green-400 to-green-500' },
  'A2': { name: 'Temel', emoji: '💪', desc: 'Güçleniyorsun!', color: 'blue', gradient: 'from-blue-400 to-blue-500' },
  'B1': { name: 'Orta', emoji: '🚀', desc: 'Yükseliyorsun!', color: 'yellow', gradient: 'from-yellow-400 to-yellow-500' },
  'B2': { name: 'Orta-İleri', emoji: '⭐', desc: 'Harikasın!', color: 'orange', gradient: 'from-orange-400 to-orange-500' },
  'C1': { name: 'İleri', emoji: '🔥', desc: 'Mükemmelsin!', color: 'red', gradient: 'from-red-400 to-red-500' },
  'C2': { name: 'Uzman', emoji: '👑', desc: 'Ustasın!', color: 'purple', gradient: 'from-purple-400 to-purple-500' }
};

const ExercisesDuolingo = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(null);
  const [progress, setProgress] = useState(null);
  const [streak, setStreak] = useState(null);
  const [dailyChallenge, setDailyChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('grammar');
  const [hoveredTopic, setHoveredTopic] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [catRes, progRes, streakRes, challengeRes] = await Promise.all([
        axios.get(`${API}/exercises/categories`),
        axios.get(`${API}/exercises/progress`, { withCredentials: true }).catch(() => ({ data: { user_id: '', total_completed: 0 } })),
        axios.get(`${API}/exercises/streak`, { withCredentials: true }).catch(() => ({ data: { current_streak: 0, longest_streak: 0 } })),
        axios.get(`${API}/exercises/daily-challenge`, { withCredentials: true }).catch(() => ({ data: { completed: false } }))
      ]);
      
      setCategories(catRes.data);
      setProgress(progRes.data);
      setStreak(streakRes.data);
      setDailyChallenge(challengeRes.data);
    } catch (error) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };
  
  const startExercise = (category, level, topicOrTheme) => {
    if (category === 'grammar') {
      navigate(`/exercises/grammar/${level}/${topicOrTheme}`);
    } else {
      navigate(`/exercises/vocabulary/${level}/${topicOrTheme}`);
    }
  };
  
  const getProgressForTopic = (category, level, topic) => {
    if (!progress) return null;
    const key = `${category}_${level.toLowerCase()}`;
    return progress[key]?.[topic];
  };
  
  const calculateCategoryProgress = (category, level) => {
    if (!progress || !categories) return 0;
    const key = `${category}_${level.toLowerCase()}`;
    const topics = category === 'grammar' ? 
      Object.keys(categories.grammar?.[level] || {}) : 
      Object.keys(categories.vocabulary?.[level] || {});
    
    const completed = topics.filter(topic => progress[key]?.[topic]?.completed).length;
    return topics.length > 0 ? (completed / topics.length) * 100 : 0;
  };
  
  // Render a single level section
  const renderLevelSection = (category, level) => {
    const config = LEVEL_CONFIG[level];
    const topics = category === 'grammar' ? categories?.grammar?.[level] : categories?.vocabulary?.[level];
    
    if (!topics || Object.keys(topics).length === 0) return null;
    
    return (
      <div key={level} className="mb-8">
        {/* Level Header */}
        <div className={`flex items-center justify-between mb-6 bg-gradient-to-r ${config.gradient} p-4 rounded-2xl shadow-lg`}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <span className={`text-2xl font-black text-${config.color}-600`}>{level}</span>
            </div>
            <div>
              <h3 className="text-2xl font-black text-white">{config.name} Seviyesi</h3>
              <p className="text-sm text-white/80">{config.desc} {config.emoji}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="bg-white px-4 py-2 rounded-full">
              <span className={`text-xl font-black text-${config.color}-600`}>
                {calculateCategoryProgress(category, level).toFixed(0)}%
              </span>
            </div>
          </div>
        </div>
        
        {/* Topics Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {Object.entries(topics).map(([key, topic]) => {
            const topicProgress = getProgressForTopic(category, level, key);
            const isCompleted = topicProgress?.completed;
            
            return (
              <div
                key={key}
                onMouseEnter={() => setHoveredTopic(`${level}-${key}`)}
                onMouseLeave={() => setHoveredTopic(null)}
                className="relative"
              >
                <Card className={`border-4 transition-all duration-300 ${
                  isCompleted 
                    ? 'border-green-400 bg-gradient-to-br from-green-50 to-green-100' 
                    : `border-${config.color}-300 bg-gradient-to-br from-${config.color}-50 to-white hover:border-${config.color}-500`
                } ${
                  hoveredTopic === `${level}-${key}` ? 'shadow-2xl -translate-y-2 scale-105' : 'shadow-lg'
                }`}>
                  <CardContent className="p-6 text-center relative">
                    {/* Completion Badge */}
                    {isCompleted && (
                      <div className="absolute -top-3 -right-3 w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg border-4 border-white animate-bounce">
                        <Crown className="w-6 h-6 text-white" />
                      </div>
                    )}
                    
                    {/* Topic Icon */}
                    <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                      isCompleted 
                        ? 'bg-gradient-to-br from-green-400 to-green-500' 
                        : `bg-gradient-to-r ${config.gradient}`
                    } shadow-xl`}>
                      <span className="text-4xl">{category === 'grammar' ? '📖' : '💬'}</span>
                    </div>
                    
                    <h4 className="font-black text-lg text-gray-800 mb-2">{topic.name}</h4>
                    <p className="text-sm text-gray-600 mb-4 min-h-[40px]">{topic.description}</p>
                    
                    {isCompleted && (
                      <div className="mb-3">
                        <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold border-0">
                          ⭐ {topicProgress.score?.toFixed(0) || 0}% puan
                        </Badge>
                      </div>
                    )}
                    
                    <Button
                      onClick={() => startExercise(category, level, key)}
                      className={`w-full h-14 text-lg font-black rounded-2xl shadow-lg transition-all ${
                        isCompleted
                          ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                          : `bg-gradient-to-r ${config.gradient} hover:opacity-90`
                      } hover:shadow-xl hover:scale-105`}
                    >
                      {isCompleted ? '🔄 Tekrar Yap' : '▶️ Başla'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="w-24 h-24 border-8 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Brain className="w-12 h-12 text-blue-600 animate-pulse" />
              </div>
            </div>
            <p className="mt-6 text-xl font-bold text-gray-700 animate-pulse">Hazırlanıyor...</p>
            <p className="text-sm text-gray-500">🎯 Dil macerası başlıyor!</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="mb-6 hover:scale-105 transition-transform border-2 border-blue-300 hover:border-blue-500"
          >
            ← Dashboard'a Dön
          </Button>
          
          <div className="text-center mb-6">
            <div className="inline-block mb-4">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-xl animate-bounce">
                <Brain className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-black mb-2">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Dil Öğren! 🚀
              </span>
            </h1>
            <p className="text-xl text-gray-600 font-semibold">A1'den C2'ye kadar tüm seviyeler!</p>
          </div>
        </div>
        
        {/* Super Stats Row */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {/* Streak */}
          <Card className="border-4 border-orange-300 bg-gradient-to-br from-orange-50 to-orange-100 hover:shadow-2xl transition-all hover:-translate-y-1">
            <CardContent className="p-6 text-center">
              <div className="relative">
                <Flame className={`w-16 h-16 mx-auto mb-2 ${streak?.current_streak > 0 ? 'text-orange-500 animate-pulse' : 'text-gray-400'}`} />
                {streak?.current_streak > 0 && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center animate-bounce">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <p className="text-5xl font-black text-orange-600 mb-1">{streak?.current_streak || 0}</p>
              <p className="text-sm font-bold text-gray-700">günlük seri! 🔥</p>
              <p className="text-xs text-gray-500 mt-1">En uzun: {streak?.longest_streak || 0}</p>
            </CardContent>
          </Card>
          
          {/* Total Completed */}
          <Card className="border-4 border-green-300 bg-gradient-to-br from-green-50 to-green-100 hover:shadow-2xl transition-all hover:-translate-y-1">
            <CardContent className="p-6 text-center">
              <Trophy className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
              <p className="text-4xl font-black text-green-600 mb-1">{progress?.total_completed || 0}</p>
              <p className="text-sm font-bold text-gray-700">tamamlandı! ✨</p>
              <div className="mt-2 flex justify-center gap-1">
                {[...Array(Math.min(5, Math.floor((progress?.total_completed || 0) / 5)))].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
            </CardContent>
          </Card>
          
          {/* Mil Points */}
          <Card className="border-4 border-purple-300 bg-gradient-to-br from-purple-50 to-purple-100 hover:shadow-2xl transition-all hover:-translate-y-1">
            <CardContent className="p-6 text-center">
              <Zap className="w-12 h-12 text-purple-500 mx-auto mb-2" />
              <p className="text-4xl font-black text-purple-600 mb-1">{(progress?.total_completed || 0) * 10}</p>
              <p className="text-sm font-bold text-gray-700">Mil Puan! ⚡</p>
              <div className="mt-2">
                <Progress value={((progress?.total_completed || 0) % 10) * 10} className="h-2" />
              </div>
            </CardContent>
          </Card>
          
          {/* Daily Challenge */}
          <Card className={`border-4 ${
            dailyChallenge?.completed 
              ? 'border-green-400 bg-gradient-to-br from-green-50 to-green-100' 
              : 'border-pink-300 bg-gradient-to-br from-pink-50 to-pink-100'
          } hover:shadow-2xl transition-all hover:-translate-y-1 cursor-pointer`}
            onClick={() => !dailyChallenge?.completed && navigate('/exercises/daily-challenge')}
          >
            <CardContent className="p-6 text-center">
              {dailyChallenge?.completed ? (
                <>
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-black text-green-600">Tamamlandı! 🎉</p>
                  <p className="text-xs text-gray-600 mt-1">Günlük görev</p>
                </>
              ) : (
                <>
                  <Target className="w-12 h-12 text-pink-500 mx-auto mb-2 animate-pulse" />
                  <p className="text-2xl font-black text-pink-600">Günlük Görev</p>
                  <p className="text-xs text-gray-600 mt-1">Tıkla ve başla! 🎯</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Main Learning Path */}
        <Card className="border-4 border-white shadow-2xl bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-8 h-16 bg-gradient-to-r from-blue-100 to-purple-100 border-2 border-blue-300">
                <TabsTrigger 
                  value="grammar" 
                  className="text-lg font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white transition-all"
                >
                  <Brain className="w-6 h-6 mr-2" />
                  Grammar 📚
                </TabsTrigger>
                <TabsTrigger 
                  value="vocabulary"
                  className="text-lg font-bold data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-purple-600 data-[state=active]:text-white transition-all"
                >
                  <BookOpen className="w-6 h-6 mr-2" />
                  Vocabulary 💬
                </TabsTrigger>
              </TabsList>
              
              {/* Grammar Tab */}
              <TabsContent value="grammar" className="space-y-8">
                {LEVELS.map(level => renderLevelSection('grammar', level))}
              </TabsContent>
              
              {/* Vocabulary Tab */}
              <TabsContent value="vocabulary" className="space-y-8">
                {LEVELS.map(level => renderLevelSection('vocabulary', level))}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* Motivational Footer */}
        <div className="mt-8 text-center">
          <div className="inline-block bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400 p-6 rounded-3xl shadow-2xl">
            <p className="text-2xl font-black text-white mb-2">🎉 Harika gidiyorsun! 🎉</p>
            <p className="text-white">A1'den C2'ye kadar yolculuğuna devam et!</p>
          </div>
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default ExercisesDuolingo;
